import {Material} from '@domain/materials'
import {EntityConfigDto, TreeNode} from '@hconnect/common/types'
import {Refresh} from '@mui/icons-material'
import {Button, Stack} from '@mui/material'
import {LocalizationProvider} from '@mui/x-date-pickers'
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment'
import React, {useEffect, useMemo} from 'react'
import {FormProvider, useForm} from 'react-hook-form'

import {useUrlParam} from '../../../routing'
import {useCreateJanusKpis} from '../../hooks/janusKpiService/useCreateJanusKpis'
import {ItemDetailsLayout} from '../../plant-setup-item-layout'
import {Config, configFieldsMap, Field, FieldType, producedReceivedMaterials} from '../../types'

import {useProductionDayBoundaries} from './hooks/useProductionDayBoundaries'
import {ProductionDayTimeFields} from './ProductionDayTimeFields'
import {SingleField} from './SingleField'
import {serializeFormValue} from './utils/formDataSerializer'
import {normalizeProductionDayTimes, PRODUCTION_DAY_DEFAULTS} from './utils/productionDayUtils'

interface PlantConfigFormProps {
  selectedItem: TreeNode
  configNotCreated: boolean
  config: EntityConfigDto
  onSubmit: (data: EntityConfigDto) => Promise<void>
  materials: Material[]
  showAllMaterials?: boolean
}

export const PlantConfigForm: React.FC<PlantConfigFormProps> = ({
  selectedItem,
  onSubmit,
  config,
  configNotCreated,
  materials
}) => {
  const defaultValues = useMemo(() => {
    const baseDefaults = {
      localName: '',
      commissEndDate: '',
      mothballed: 'false',
      commissStartDate: '',
      useCustomProductionDayBoundaries: 'false',
      productionDayStartTime: PRODUCTION_DAY_DEFAULTS.START_TIME,
      productionDayEndTime: PRODUCTION_DAY_DEFAULTS.END_TIME
    } as Config

    const shouldReturnDefaults =
      configNotCreated ||
      !config ||
      !config.payload?.productionData ||
      config.payload.productionData.length === 0

    if (shouldReturnDefaults) {
      return baseDefaults
    }

    const loadedConfig = config.payload.productionData.reduce(
      (acc, item) => {
        acc[item.name] = item.value
        return acc
      },
      {} as Record<string, string>
    ) as Config

    return normalizeProductionDayTimes({
      ...baseDefaults,
      ...loadedConfig
    })
  }, [config, configNotCreated])

  const methods = useForm({
    mode: 'all',
    shouldFocusError: false,
    defaultValues
  })

  const {
    handleSubmit,
    watch,
    trigger,
    setValue,
    getValues,
    reset,
    formState: {isDirty, isSubmitSuccessful, isSubmitting, isValid}
  } = methods

  const {mutateAsync: createJanusKpis, isPending} = useCreateJanusKpis()
  const plantCode = useUrlParam('plantCode')

  const shouldDisplayKpiCreationButton = process.env.REACT_APP_STAGE !== 'prod'
  const received = watch('receivedMaterials')
  const produced = watch('producedMaterials')
  const watchMothballed = watch('mothballed')
  const watchUseCustomProductionDayBoundaries = watch('useCustomProductionDayBoundaries')
  const watchProductionDayStartTime = watch('productionDayStartTime')

  useProductionDayBoundaries(
    getValues,
    setValue,
    trigger,
    watchUseCustomProductionDayBoundaries,
    watchProductionDayStartTime
  )

  const filteredFields = useMemo(() => {
    const producedValues = produced ? produced.split('";"') : []
    const receivedValues = received ? received.split('";"') : []

    const fieldsToHide = producedReceivedMaterials
      .filter(
        (material) =>
          !(receivedValues.includes(material.id) || producedValues.includes(material.id))
      )
      .map((field) => field.fieldName)

    const producedFields = producedReceivedMaterials
      .filter((material) => producedValues.includes(material.id))
      .map((field) => field.fieldName)

    const receivedFields = producedReceivedMaterials
      .filter((material) => receivedValues.includes(material.id))
      .map((field) => field.fieldName)

    const filtered = configFieldsMap['Plant'].filter((field) => !fieldsToHide.includes(field.name))

    filtered.forEach((field: FieldType) => {
      if (producedFields.includes(field.name)) {
        field.optional = false
      } else {
        if (receivedFields.includes(field.name)) {
          field.optional = true
        }
      }
    })

    return filtered
  }, [produced, received])

  useEffect(() => {
    trigger()
  }, [watchMothballed, received, produced, trigger])

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(defaultValues)
    }
  }, [isSubmitSuccessful, defaultValues, reset])

  const submit = async (data: Config) => {
    if (!isDirty) return
    const newConfig = structuredClone(config)

    newConfig.payload.productionData = Object.keys(data).map((key) => ({
      name: key,
      value: serializeFormValue(data[key])
    }))

    await onSubmit(newConfig)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <FormProvider {...methods}>
        <ItemDetailsLayout
          saveButton={{
            hide: false,
            onClick: handleSubmit(submit),
            disabled: isSubmitting || !isDirty || !isValid,
            ButtonProps: {
              loadingPosition: 'start',
              loading: isSubmitting
            }
          }}
        >
          <Stack maxWidth="500px" spacing={3}>
            {shouldDisplayKpiCreationButton && (
              <Button
                color="info"
                variant="contained"
                startIcon={<Refresh />}
                style={{maxWidth: 200}}
                onClick={() => createJanusKpis({plantCode})}
                disabled={isPending}
              >
                Update Kpis
              </Button>
            )}
            {filteredFields.map((field: Field) => {
              if (field.name === 'productionDayEndTime') {
                return null
              }

              if (field.name === 'productionDayStartTime') {
                const endTimeField = filteredFields.find((f) => f.name === 'productionDayEndTime')

                if (!endTimeField) {
                  return (
                    <SingleField
                      key={field.name}
                      field={field}
                      materials={materials}
                      upmId={selectedItem.upmId}
                    />
                  )
                }

                return (
                  <ProductionDayTimeFields
                    key={field.name}
                    field={field}
                    endTimeField={endTimeField}
                    materials={materials}
                    upmId={selectedItem.upmId}
                  />
                )
              }

              return (
                <SingleField
                  key={field.name}
                  field={field}
                  materials={materials}
                  upmId={selectedItem.upmId}
                />
              )
            })}
          </Stack>
        </ItemDetailsLayout>
      </FormProvider>
    </LocalizationProvider>
  )
}
